import debug from 'debug';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePageVisibility } from 'react-page-visibility';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { LOCAL_EVENT, STORAGE_EVENT } from '@/config/constants';
import { useEventListener } from '@/hooks/useEventListener';
import { useSocket } from '@/hooks/useSocket';
import { avatarSelector, versionSelector, widgetIdSelector, } from '@/state/config';
import { automaticMessageSelector, automaticMessageStorageKeySelector, chatEventsSelector, chatEventsState, composeState, nextQueuedEventSelector, isLastMessageReadSelector, isLeaderSelector, isWidgetOpenState, brainEventsQueue, } from '@/state/session';
import * as safeStorage from '@/util/safe-storage';
import { sleep } from '@/util/utils';
/**
 * This hooks abstracts the behavior of reading messages when the user is viewing the chat.
 * It sends the automatic message when the chat is open.
 */
export const useChatEvents = () => {
    const isVisible = usePageVisibility();
    const { sendMessage, markMessagesAsRead } = useSocket();
    // The events here are filtered to only include the events that are relevant to the chat.
    const chatEvents = useRecoilValue(chatEventsSelector);
    const isLastMessageRead = useRecoilValue(isLastMessageReadSelector);
    const isOpen = useRecoilValue(isWidgetOpenState);
    const isLeader = useRecoilValue(isLeaderSelector);
    const widgetId = useRecoilValue(widgetIdSelector);
    const automaticMessage = useRecoilValue(automaticMessageSelector);
    const messageKey = useRecoilValue(automaticMessageStorageKeySelector);
    const setChatEvents = useSetRecoilState(chatEventsState);
    const setBrainResponses = useSetRecoilState(brainEventsQueue);
    const nextQueuedEvent = useRecoilValue(nextQueuedEventSelector);
    const setComposeState = useSetRecoilState(composeState);
    const avatar = useRecoilValue(avatarSelector);
    const log = useMemo(() => debug(`moveo:iframe:${widgetId}:session-id`), [widgetId]);
    const version = useRecoilValue(versionSelector);
    // In order to stick to the order that the events come, we add all the incoming messages from the brain
    // to the messagesQueue and then we delay the typing of the brain. This way we can add the corresponding
    // delay to each message and the order will be preserved.
    const ref = useRef('');
    useEffect(() => {
        // Check if the current message has been already processed
        if (ref.current === (nextQueuedEvent === null || nextQueuedEvent === void 0 ? void 0 : nextQueuedEvent.event_id) || !nextQueuedEvent) {
            return;
        }
        const delayMessages = async () => {
            // Show brain is typing
            var _a, _b;
            const shouldShowTyping = (nextQueuedEvent === null || nextQueuedEvent === void 0 ? void 0 : nextQueuedEvent.event_type) === 'brain_message';
            const startTyping = {
                event_id: nanoid(),
                event_type: 'compose',
                author_name: 'common.ai_agent',
                author_type: 'brain',
                author_avatar: avatar,
                author_id: nanoid(),
                action: 'start',
            };
            const stopTyping = Object.assign(Object.assign({}, startTyping), { event_id: nanoid(), action: 'stop' });
            const existingBrainEvents = chatEvents.filter((e) => e.event_type === 'brain_message' &&
                e.request_id === (nextQueuedEvent === null || nextQueuedEvent === void 0 ? void 0 : nextQueuedEvent.request_id));
            if (version === 'v2') {
                if (shouldShowTyping && existingBrainEvents.length === 0) {
                    setComposeState(startTyping);
                }
            }
            else {
                if (shouldShowTyping) {
                    setComposeState(startTyping);
                }
            }
            // Adds the corresponding delay to the message, then adds it to the chat events
            if (((_a = nextQueuedEvent === null || nextQueuedEvent === void 0 ? void 0 : nextQueuedEvent.response) === null || _a === void 0 ? void 0 : _a.type) !== 'replay' &&
                (nextQueuedEvent === null || nextQueuedEvent === void 0 ? void 0 : nextQueuedEvent.event_type) === 'brain_message') {
                const eventParams = nextQueuedEvent === null || nextQueuedEvent === void 0 ? void 0 : nextQueuedEvent.response;
                const duration = (_b = eventParams === null || eventParams === void 0 ? void 0 : eventParams.duration) !== null && _b !== void 0 ? _b : 1;
                await sleep(duration * 1000);
            }
            setChatEvents((currentState) => [...currentState, nextQueuedEvent]);
            // Removes processed message from the queue
            setBrainResponses((prev) => prev.filter((e) => e.event_id !== nextQueuedEvent.event_id));
            // Brain stops typing
            if (shouldShowTyping) {
                setComposeState(stopTyping);
            }
        };
        delayMessages();
        // Saves last processed event
        ref.current = nextQueuedEvent === null || nextQueuedEvent === void 0 ? void 0 : nextQueuedEvent.event_id;
    }, [
        setBrainResponses,
        setChatEvents,
        nextQueuedEvent,
        avatar,
        setComposeState,
        ref,
        version,
        chatEvents,
    ]);
    // Mark messages as read when the user is viewing the chat.
    useEffect(() => {
        if (isVisible && isOpen && !isLastMessageRead) {
            markMessagesAsRead();
        }
    }, [isLastMessageRead, isOpen, isVisible, markMessagesAsRead]);
    // Check if the leader is there and send the automatic message.
    const handlePendingMessage = useCallback((event) => {
        const e = event;
        if (e.key === messageKey) {
            log(event, `automatic by ${isLeader ? 'leader' : 'not the leader'}`);
            if (isLeader && automaticMessage && e.newValue && !e.oldValue) {
                safeStorage.removeItem(messageKey);
                sendMessage(automaticMessage);
            }
        }
    }, [automaticMessage, isLeader, log, messageKey, sendMessage]);
    useEventListener(STORAGE_EVENT, handlePendingMessage);
    useEventListener(LOCAL_EVENT, handlePendingMessage);
    useEffect(() => {
        if (automaticMessage && isOpen) {
            safeStorage.setItem(messageKey, 'true');
            // Dispatch a custom event in case this is the leader
            window.dispatchEvent(new StorageEvent(LOCAL_EVENT, {
                key: messageKey,
                url: 'local',
                newValue: 'true',
            }));
        }
        return () => {
            safeStorage.removeItem(messageKey);
        };
    }, [automaticMessage, isLeader, isOpen, messageKey, sendMessage]);
    return chatEvents;
};
